import React, { useState } from "react";
import { MapPin } from "lucide-react";
import { projects, gallery } from "../mock";
import { uploadedPhotoBySrc } from "../data/uploadedPhotos";
import useScrollReveal from "../hooks/useScrollReveal";
import { useTranslation } from "react-i18next";

const categoryOrder = ["Hoteli", "Wellness", "Rezidencije", "Poslovno", "Sport", "Klinike", "Restorani"];

const Properties = () => {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState("Sve");
  useScrollReveal(`${active}:${i18n.resolvedLanguage}`);
  const galleryFilters = ["Sve", ...gallery.map((g) => g.category)];

  const shownGallery = active === "Sve" ? gallery : gallery.filter((g) => g.category === active);

  // Group projects by category
  const grouped = categoryOrder
    .map((cat) => ({ cat, items: projects.filter((p) => p.category === cat) }))
    .filter((g) => g.items.length > 0);

  return (
    <main className="pt-[72px]">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24">
        <span data-testid="projects-eyebrow" className="inline-block px-4 py-1.5 rounded-full border border-black/25 text-[10px] font-medium uppercase tracking-[0.16em] mb-8">
          {t("projectsPage.eyebrow")}
        </span>
        <h1 data-testid="projects-heading" className="font-display font-bold text-[2.5rem] sm:text-6xl md:text-7xl lg:text-8xl xl:text-[9rem] leading-[0.88] tracking-tight mb-6">
          {t("projectsPage.title")}
        </h1>
        <p className="font-display text-[clamp(1.2rem,3vw,2.2rem)] leading-[1.2] tracking-tight max-w-3xl text-balance">
          <span data-testid="projects-intro-lead">{t("projectsPage.lead")} </span>
          <span data-testid="projects-intro-detail" className="text-[#0e0e10]/35">{t("projectsPage.muted")}</span>
        </p>
      </section>

      {/* PROJECT LIST */}
      <section data-testid="projects-text-list" className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        {grouped.map((group, gi) => (
          <div key={group.cat} data-testid={`projects-group-${group.cat.toLowerCase()}`} className="reveal-on-scroll mb-14 last:mb-0" style={{ "--reveal-delay": `${gi * 60}ms` }}>
            <div className="flex items-baseline gap-4 mb-5">
              <h2 data-testid={`projects-category-${group.cat.toLowerCase()}`} className="font-display text-2xl md:text-3xl tracking-tight">{t(`categories.${group.cat}`)}</h2>
              <span data-testid={`projects-count-${group.cat.toLowerCase()}`} className="text-[#0057FF] font-display text-sm">{String(group.items.length).padStart(2, "0")}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8">
              {group.items.map((p, i) => (
                <div
                  key={p.id}
                  data-testid={`project-row-${p.id}`}
                  style={{ "--reveal-delay": `${(i % 3) * 90}ms` }}
                  className="reveal-on-scroll group flex items-center justify-between border-t border-black/12 py-5 hover:border-black/40 transition-colors"
                >
                  <div className="min-w-0">
                    <h3 data-testid={`project-title-${p.id}`} className="font-display text-lg tracking-tight group-hover:translate-x-1 transition-transform duration-300">
                      {t(`projectNames.${p.id}`)}
                    </h3>
                    <p data-testid={`project-location-${p.id}`} className="text-[12px] uppercase tracking-[0.1em] text-[#0e0e10]/50 mt-1 flex items-center gap-1.5">
                      <MapPin size={12} /> {p.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* GALLERY */}
      <section data-testid="projects-gallery" className="bg-[#0e0e10] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <h2 data-testid="projects-gallery-heading" className="font-display font-bold text-[clamp(2rem,7vw,5rem)] leading-[0.9] tracking-tight whitespace-pre-line">
              {t("projectsPage.galleryTitle")}
            </h2>
            <p data-testid="projects-gallery-intro" className="text-[13px] text-white/50 max-w-sm leading-relaxed mb-2">
              {t("projectsPage.galleryIntro")}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-b border-white/12 pb-8 mb-10">
            {galleryFilters.map((f) => (
              <button
                key={f}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                aria-pressed={active === f}
                onClick={() => setActive(f)}
                className={`h-10 px-6 rounded-full text-[11px] font-medium uppercase tracking-[0.12em] transition-colors duration-300 ${
                  active === f ? "bg-white text-[#0e0e10]" : "border border-white/25 text-white/80 hover:bg-white/10"
                }`}
              >
                {t(`categories.${f}`)}
              </button>
            ))}
          </div>

          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {shownGallery.flatMap((g) =>
              g.images.map((src, idx) => (
                <div
                  key={g.category + src}
                  style={{ "--reveal-delay": `${idx * 90}ms` }}
                  className="reveal-on-scroll img-zoom-wrap relative overflow-hidden rounded-sm mb-4 break-inside-avoid"
                >
                  <img src={src} data-testid={`gallery-image-${uploadedPhotoBySrc[src]?.id || `${g.category.toLowerCase()}-${idx}`}`} alt={uploadedPhotoBySrc[src] ? t(`photos.${uploadedPhotoBySrc[src].id}`) : t("common.galleryImage", { category: t(`categories.${g.category}`) })} width={uploadedPhotoBySrc[src]?.width} height={uploadedPhotoBySrc[src]?.height} className="img-zoom w-full h-auto object-cover" loading="lazy" />
                  <span data-testid={`gallery-category-${uploadedPhotoBySrc[src]?.id || `${g.category.toLowerCase()}-${idx}`}`} className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] font-medium uppercase tracking-[0.12em]">
                    {t(`categories.${g.category}`)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Properties;
